import { TFunction } from "i18next";
import { UserAccount } from "src/types/User";

/**
 * Short, per-reason copy for the application-result screens. Keyed off the
 * `code` the server stores on each disqualifier (DisqualifierCode in
 * functions/utils/checkForApplicationDisqualifiers.ts). Decisions without a
 * code, or with a code not listed here, fall back to generic copy or to the
 * stored long-form reason.
 */

type Disqualifier = { code?: string; reason: string; blogLink?: string };

/**
 * Where CDL training runs. The copy names these places so a friend knows if
 * they can train. Update when a CDL program opens or closes.
 */
export function programAreas(t: TFunction): string {
  return t("We train people in NYC, Massachusetts, and the Sacramento area.");
}

/** "Know someone in NYC?" for the student's own area, else "there". */
export function referralQuestion(t: TFunction, user: UserAccount): string {
  switch (user.state) {
    case "NY":
      return t("Know someone in NYC?");
    case "MA":
      return t("Know someone in Massachusetts?");
    case "CA":
      return t("Know someone in the Sacramento area?");
    default:
      return t("Know someone there?");
  }
}

export type RejectionCopy = {
  /** Why they don't qualify, in one short sentence. */
  reason: string;
  /** Lead-in before the update-application link. */
  fixLead: string;
  /** The link itself; lowercase when the lead-in ends mid-sentence. */
  linkLabel: string;
};

/**
 * The first rejected code decides the copy. The server checks citizenship,
 * then area, then justice involvement, then income, so the first code is the
 * one that matters most. With no known code (a staff rejection, an older
 * decision) the stored reason is shown when there is one.
 */
export function rejectionCopy(
  t: TFunction,
  disqualifiers: Disqualifier[],
  storedReason?: string
): RejectionCopy {
  const codes = disqualifiers.map((d) => d.code);
  const changedLead = t("Did we get this wrong?");
  const linkLabel = t("Update your application here.");

  if (codes.includes("citizenship")) {
    return {
      reason: t("Your citizenship status doesn't fit this program's funding."),
      fixLead: t("Did your status change?"),
      linkLabel,
    };
  }
  if (codes.includes("state_not_served")) {
    return { reason: programAreas(t), fixLead: t("Moved?"), linkLabel };
  }
  if (codes.includes("city_not_served")) {
    return {
      reason: t("We don't train in your city yet."),
      fixLead: t("Moved?"),
      linkLabel,
    };
  }
  if (
    codes.includes("justice_involvement_required") ||
    codes.includes("justice_criteria_not_met")
  ) {
    return {
      reason: t(
        "This program is funded for people who've been involved with the justice system."
      ),
      fixLead: changedLead,
      linkLabel,
    };
  }
  if (codes.includes("justice_impacted_required")) {
    return {
      reason: t(
        "This program is funded for people impacted by the justice system."
      ),
      fixLead: changedLead,
      linkLabel,
    };
  }
  if (codes.includes("income_over_limit")) {
    return {
      reason: t("Your income is over this program's limit."),
      fixLead: t("If your income changes,"),
      linkLabel: t("update your application here."),
    };
  }
  return {
    reason: storedReason || t("You don't qualify for this program right now."),
    fixLead: changedLead,
    linkLabel,
  };
}

export type BarrierStep = {
  title: string;
  detail: string;
  link?: { label: string; href: string };
};

const MEDICAL_CODES = [
  "vision",
  "sleep_apnea",
  "epilepsy",
  "oxygen_therapy",
  "cardiac",
  "high_blood_pressure",
  "medications",
];
const DRIVING_RECORD_CODES = [
  "accident_record",
  "multiple_duis",
  "serious_violations",
  "points_suspension",
];
const CRIMINAL_RECORD_CODES = [
  "sex_offense",
  "drug_trafficking",
  "recent_felonies",
  "serious_felony",
];

function barrierStep(t: TFunction, d: Disqualifier): BarrierStep {
  const link = (label: string) =>
    d.blogLink ? { label, href: d.blogLink } : undefined;
  const code = d.code ?? "";

  if (code === "no_license") {
    return {
      title: t("Get your driver's license."),
      detail: t(
        "You need it for your CDL permit. Keep training while you get it."
      ),
    };
  }
  if (code === "license_not_active") {
    return {
      title: t("Get your license reinstated."),
      detail: t("Keep training while you work on it."),
    };
  }
  if (code === "license_out_of_state") {
    return {
      title: t("Move your license to the state you live in."),
      detail: t("Keep training while you do it at the DMV."),
    };
  }
  if (code === "pending_case") {
    return {
      title: t("Close out your pending case."),
      detail: t(
        "Keep training. We can enroll you in school once it's resolved."
      ),
    };
  }
  if (code === "probation_travel") {
    return {
      title: t("Get your officer's OK to drive across state lines."),
      detail: t(
        "Keep training while you ask. Most trucking jobs cross state lines."
      ),
      link: link(t("How to ask your officer")),
    };
  }
  if (code === "wellbeing_check_incomplete") {
    return {
      title: t("Finish the wellbeing check."),
      detail: t("It's part of your application."),
    };
  }
  if (MEDICAL_CODES.includes(code)) {
    return {
      title: t("Get a note from your doctor."),
      detail: t(
        "It should say you're cleared to drive a truck. Keep training while you get it."
      ),
      link: link(t("See what the note needs")),
    };
  }
  if (DRIVING_RECORD_CODES.includes(code)) {
    return {
      title: t("Your driving record can make hiring harder."),
      detail: t("Keep training. Here's what you can do about it."),
      link: link(t("See your options")),
    };
  }
  if (CRIMINAL_RECORD_CODES.includes(code)) {
    return {
      title: t("Your record can make hiring harder."),
      detail: t("Keep training. Here's what you can do about it."),
      link: link(t("See your options")),
    };
  }
  if (code === "failed_dot_drug_test") {
    return {
      title: t("A failed DOT drug test can make hiring harder."),
      detail: t("Keep training. Here's what you can do about it."),
      link: link(t("See your options")),
    };
  }
  // No code (older decision) or a code this screen doesn't know yet: show
  // the stored reason so the student still sees what to clear.
  return { title: d.reason, detail: "", link: link(t("Learn more")) };
}

/**
 * One step per barrier. Several medical (or record) codes collapse into one
 * step, because they share the same thing to do.
 */
export function barrierSteps(
  t: TFunction,
  disqualifiers: Disqualifier[]
): BarrierStep[] {
  const seen = new Set<string>();
  return disqualifiers
    .map((d) => barrierStep(t, d))
    .filter((step) => {
      if (seen.has(step.title)) return false;
      seen.add(step.title);
      return true;
    });
}
